import type { QuickView } from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';

import { analyticsApi } from 'apis/analytics';
import { appActions } from 'app/app.actions';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';

type UseQuickViewActionsOptions = {
  /** The location from where the quick view actions are triggered. */
  location: 'quickView' | 'quickViews';
};

type UseQuickViewActionsResult = {
  /** The handler to update a quick view. */
  handleUpdateQuickView: (
    quickView: QuickView,
  ) => Promise<{ quickView: QuickView }>;
  /** The handler to duplicate a quick view. */
  handleDuplicateQuickView: (quickView: QuickView) => void;
  /** The handler to delete a quick view. */
  handleDeleteQuickView: (quickView: QuickView) => Promise<{ isDone: boolean }>;
  /** The handler to toggle a quick view's favorite state. */
  handleUpdateQuickViewFavoriteState: (quickView: QuickView) => Promise<void>;
};

export const useQuickViewActions = ({
  location,
}: UseQuickViewActionsOptions): UseQuickViewActionsResult => {
  const dispatch = useAppDispatch();
  const notify = useNotifications();

  const [deleteQuickView] = analyticsApi.useDeleteQuickViewMutation();
  const [updateQuickViewFavoriteState] =
    analyticsApi.useUpdateQuickViewFavoriteStateMutation();

  /** Handles updating a quick view. */
  const handleUpdateQuickView: UseQuickViewActionsResult['handleUpdateQuickView'] =
    async (quickView) => {
      return await dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'update',
              dashboardId: quickView.dashboard_id,
              quickView,
              location,
            },
          },
        }),
      );
    };

  /** Handles creating a new quick view based on an existing one. */
  const handleDuplicateQuickView: UseQuickViewActionsResult['handleDuplicateQuickView'] =
    (quickView) => {
      dispatch(
        appActions.showModal({
          modal: {
            type: 'analytics:quickView',
            arg: {
              mode: 'duplicate',
              dashboardId: quickView.dashboard_id,
              quickView,
              location,
            },
          },
        }),
      );
    };

  /** Handles deleting a quick view. */
  const handleDeleteQuickView: UseQuickViewActionsResult['handleDeleteQuickView'] =
    async (quickView) => {
      const { isConfirmed } = await dispatch(
        appActions.showModal({
          modal: {
            type: 'app:confirm',
            arg: {
              title: `Delete "${quickView.name}"`,
              description: 'Are you sure you want to delete this quick view?',
              confirm: {
                label: 'Delete',
              },
            },
          },
        }),
      );

      if (isConfirmed) {
        try {
          await deleteQuickView({
            id: quickView.id,
            dashboardId: quickView.dashboard_id,
          });

          notify(
            {
              title: 'Success',
              text: `"${quickView.name}" has been deleted.`,
              icon: 'i-done',
              time: 5000,
            },
            NotificationType.Success,
          );

          ampli.analyticsQuickViewDeleted({ shared: quickView.shared });

          return Promise.resolve({ isDone: true });
        } catch (error) {
          notify(
            {
              title: 'Error while deleting the quick view.',
            },
            NotificationType.Error,
          );
        }
      }

      return Promise.resolve({ isDone: false });
    };

  /** Handles updating the favorite state of a quick view. */
  const handleUpdateQuickViewFavoriteState: UseQuickViewActionsResult['handleUpdateQuickViewFavoriteState'] =
    async (quickView) => {
      const { favorited } = quickView;

      try {
        await updateQuickViewFavoriteState({
          quickView,
          favorited: !favorited,
        }).unwrap();

        ampli.analyticsQuickViewFavoriteToggled({ favorited: !favorited });
      } catch (error) {
        notify(
          {
            title: `"${quickView.name}" could not be ${
              favorited ? 'unfavorited' : 'favorited'
            }.`,
          },
          NotificationType.Error,
        );
      }
    };

  return {
    handleUpdateQuickView,
    handleDuplicateQuickView,
    handleDeleteQuickView,
    handleUpdateQuickViewFavoriteState,
  };
};
